import { isClient, removeElementTags } from '@qcwp/utils'
import { ARTICLE_TYPE, LOAD_STATUS } from '@qcwp/common'
import { ArticleDetail } from './class'
import { turnJumpCheck } from './hook'
import { getArticleDetail, getBindTopics, getUserArticleDetail } from '~~/src/server/modules/article'
import type { BindTopicsParams } from '~~/src/server/modules/article/type'
import { NEWS_HOME_SITE_META, VIDEO_HOME_SITE_META } from '~~/src/common/site'
import type { ShareBase } from '~~/src/composables/useShare'
import { articleApi } from '~~/src/server'
import { useGetRouteQuery } from '~~/src/common/function/url'
import { ArticleShareBase } from '~~/src/composables/useShare'
import type { MpQrcodeParams } from '~~/src/server/modules/base/types'
import { getMpQrcode } from '~~/src/server/modules/base'

export * from './hook'
export * from './class'

export function LogicDetailQrcode() {
  async function getQrcode(params: MpQrcodeParams) {
    const { data } = await getMpQrcode(params)
    if (data)
      return `data:image/png;base64,${data}`

    return ''
  }

  return {
    getQrcode,
  }
}

export function LogicArticleDetail() {
  const route = useRoute()
  // 作者预览
  const isAuthor = useGetRouteQuery('user', 'string')

  const { getQrcode } = LogicDetailQrcode()
  const detail = ref<ArticleDetail | null>(null)
  const media = computed(() => {
    if (!detail.value)
      return null
    const data = detail.value
    return {
      mediaId: data?.mediaUserId,
      mediaName: data?.mediaName,
      mediaAvatar: data?.mediaAvatar,
      mediaType: data?.mediaType,
      mediaGrade: data?.mediaGrade,
      isFocus: data?.focus,
    }
  })
  const detailType = computed(() => detail.value ? detail.value.articleType : undefined)
  const status = ref<LOAD_STATUS>(LOAD_STATUS.INIT)

  const shareBase = ref<ShareBase | null>(null)
  const { siteMeta } = useSite(NEWS_HOME_SITE_META)
  function generateSiteMate(detail: ArticleDetail) {
    const BASE_MATE = [ARTICLE_TYPE.VIDEO].includes(detail.articleType) ? VIDEO_HOME_SITE_META : NEWS_HOME_SITE_META
    siteMeta.value.title = `${detail.title}_${detail.mediaName}`
    siteMeta.value.description = `${getDescription(detail)}_${BASE_MATE.description}`
    siteMeta.value.keywords = [
      ...BASE_MATE.keywords,
      ...detail.articleTags,
      ...detail.articleDetailInfoVO.splitKeyword(),
      detail.mediaName,
      // TODO car info
    ]
  }

  function getDescription(detail: ArticleDetail) {
    const summary = detail.articleDetailInfoVO.metaDescription || detail.summary || detail.title
    return summary + removeElementTags(detail.articleDetailInfoVO.articleContent).slice(0, 100)
  }

  async function serverHandler(id: string) {
    status.value = LOAD_STATUS.LOADING
    const fn = isAuthor.value === '1' ? getUserArticleDetail : getArticleDetail
    const asyncData = await useAsyncData(`ARTICLE_${id}`, () => fn(id))
    const { code, data, error } = useNuxtAsyncHandle(asyncData)

    if (error?.value)
      return status.value = LOAD_STATUS.ERROR

    if (code === 200 && data) {
      status.value = LOAD_STATUS.END
      detail.value = new ArticleDetail(data)

      // 统计查看文章
      addLookHandler(id)

      generateSiteMate(detail.value)
      shareBase.value = ArticleShareBase.generateShareInfo(detail.value)
    }
    else {
      status.value = LOAD_STATUS.NULL
    }
  }

  /**
   * 获取文章绑定的专题列表
   */
  async function bindTopicsServerHandler(params: BindTopicsParams) {
    const asyncData = await useAsyncData(`ARTICLE_BIND_TOPIC_${qs.stringify(params)}`, () => getBindTopics(params))
    const { data } = useNuxtAsyncHandle(asyncData)
    return data ?? []
  }

  function addLookHandler(id: string) {
    if (!isClient)
      return
    articleApi.AddArticleLookCount(id).catch()
    if (detail.value?.articleType === ARTICLE_TYPE.PICTURE) {
      getQrcode({ envVersion: 'release', page: 'subpages/pages/article/index', scene: id }).then((qrcode) => {
        detail.value?.setMiniQrcode(qrcode || '')
      })
    }

    turnJumpCheck(detail.value, route.name as string || '')
  }

  return {
    isAuthor,
    status,
    detail,
    detailType,
    siteMeta,
    media,
    shareBase,
    serverHandler,
    bindTopicsServerHandler,
  }
}
